import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { FiCheckCircle, FiArrowRight } from "react-icons/fi";
import { TRAINING_INTRO, TRAINING_CATEGORIES } from "../../data/trainingData";

const TrainingIntro = () => {
  return (
    <section className="bg-white py-14 md:py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <motion.div
            initial={{ opacity: 0, x: -24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="lg:col-span-7"
          >
            <span className="inline-block text-sm font-semibold text-[#F86624] uppercase tracking-wide mb-3">
              {TRAINING_INTRO.badge}
            </span>
            {TRAINING_INTRO.brandLine && (
              <p className="text-base md:text-lg font-semibold text-[var(--bg-primary)] mb-2">
                {TRAINING_INTRO.brandLine}
              </p>
            )}
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight">
              {TRAINING_INTRO.title}
            </h1>
            <div
              className="w-16 h-1 bg-[#F86624] rounded-full mb-6"
              aria-hidden="true"
            />
            {TRAINING_INTRO.paragraphs.map((paragraph, i) => (
              <p
                key={i}
                className="text-gray-600 text-base md:text-lg leading-relaxed mb-4 last:mb-0"
              >
                {paragraph}
              </p>
            ))}

            {TRAINING_INTRO.relatedServices?.length > 0 && (
              <div className="mt-8 pt-6 border-t border-gray-100">
                <h3 className="text-sm font-bold uppercase tracking-wider text-gray-400 mb-3">
                  Related Services
                </h3>
                <div className="flex flex-wrap gap-3">
                  {TRAINING_INTRO.relatedServices.map((service) => (
                    <Link
                      key={service.link}
                      to={service.link}
                      className="inline-flex items-center gap-1.5 text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
                    >
                      {service.label}
                      <FiArrowRight size={14} aria-hidden="true" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 24 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.1 }}
            className="lg:col-span-5 space-y-6"
          >
            <div className="bg-gray-50 rounded-2xl border border-gray-100 p-6 md:p-8 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                Delivery Options
              </h2>
              <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
              <ul className="space-y-4">
                {TRAINING_INTRO.deliveryModes.map((mode) => (
                  <li key={mode} className="flex items-start gap-3">
                    <FiCheckCircle
                      size={20}
                      className="text-[var(--bg-primary)] flex-shrink-0 mt-0.5"
                      aria-hidden="true"
                    />
                    <span className="text-gray-700 font-medium">{mode}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white rounded-2xl border border-gray-100 p-6 md:p-7 shadow-sm">
              <h2 className="text-lg font-bold text-gray-900 mb-3">
                Popular Training Pathways
              </h2>
              <div className="w-16 h-1 bg-[#F86624] rounded-full mb-5" aria-hidden="true" />
              <ul className="space-y-2.5">
                {TRAINING_CATEGORIES.slice(0, 6).map((category) => (
                  <li key={category.id}>
                    <Link
                      to={category.link}
                      className="group flex items-center justify-between gap-2 text-sm text-gray-700 hover:text-[var(--bg-primary)] transition-colors py-1.5 border-b border-gray-50 last:border-0"
                    >
                      <span>{category.title}</span>
                      <FiArrowRight
                        size={14}
                        className="opacity-0 group-hover:opacity-100 text-[#F86624] transition-opacity flex-shrink-0"
                        aria-hidden="true"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
              <a
                href="#training-categories"
                className="inline-flex items-center gap-1.5 mt-4 text-sm font-semibold text-[var(--bg-primary)] hover:text-[#F86624] transition-colors"
              >
                View all pathways
                <FiArrowRight size={14} aria-hidden="true" />
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default TrainingIntro;
